'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  TrendingDown,
  Zap,
  BarChart3,
  AlertTriangle,
  Clock,
  DollarSign,
  FileCheck,
  Activity,
  Gauge,
  Car,
  CreditCard,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Target,
  Users,
  Building2,
  Lock,
  Award,
  LineChart,
  Database,
  Truck,
} from 'lucide-react';
import { PAGE_HERO_IMAGES } from '@/lib/marketing-images';
import {
  INSURANCE_SERVICE_MODULE_KEYS,
  TENANT_PACKAGE_LABELS,
} from '@/lib/industry-packages';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const staggerContainer = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { staggerChildren: 0.1 },
};

const insurancePackageModuleLabel: Record<string, string> = {
  ubi: 'UBI Risk Scoring',
  'insurance-telemetry': 'Insurance Telemetry',
  'insurance-crash-claims': 'Crash & Claims Automation',
  'insurance-risk-portfolio': 'Risk Portfolio Console',
  'insurance-underwriting': 'Underwriting Workbench',
  'insurance-api-gateway': 'Insurance API Gateway',
  'insurance-fraud-compliance': 'Fraud & Compliance',
  'insurance-leaderboard-incentive': 'Leaderboard & Incentives',
  billing: 'Billing Console',
  reports: 'Reports & Analytics',
};

const insuranceOnboardingSequence = [
  'Select Insurance Telematics industry and package.',
  'Choose plan + workload sizing for insurer operations.',
  'Review required insurance modules and launch tenant.',
] as const;

const insurancePackageModules = [
  ...INSURANCE_SERVICE_MODULE_KEYS,
  'billing',
  'reports',
];

export default function InsuranceUBIPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* ══════════════════════════════════════════════════════
          HERO SECTION
          ══════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden gradient-hero noise-overlay grid-bg pt-24 pb-20 lg:pt-32 lg:pb-28">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full opacity-20 blur-[120px]" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.3), transparent 70%)' }} />
        <div className="absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full opacity-15 blur-[100px]" style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.4), transparent 70%)' }} />

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-purple mb-6">
              <ShieldCheck className="h-3 w-3" />
              Insurance Telematics & UBI Platform
            </span>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl mb-6"
            >
              Transform Insurance with
              <br />
              <span className="text-gradient">Real-Time Telematics</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mx-auto max-w-3xl text-lg leading-relaxed text-slate-300 mb-8"
            >
              Revolutionize your insurance operations with AI-powered UBI scoring, automated crash detection,
              instant claims processing, and real-time risk profiling. Reduce fraud by 40%, improve pricing accuracy by 25%,
              and process claims 60% faster with GlonexTrack&apos;s comprehensive telematics platform.
            </motion.p>

            <div className="mx-auto mb-8 max-w-4xl">
              <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
                <Image
                  src={PAGE_HERO_IMAGES.insuranceUbi.src}
                  alt={PAGE_HERO_IMAGES.insuranceUbi.alt}
                  width={1600}
                  height={900}
                  className="h-auto w-full rounded-2xl object-cover"
                  priority
                />
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col items-center justify-center gap-4 sm:flex-row"
            >
              <Link href="/signup?industry=insurance&package=insurance">
                <button className="btn-neon rounded-full px-8 py-3.5 text-sm font-semibold flex items-center gap-2 shadow-lg">
                  Start Free Trial
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link href="#demo">
                <button className="btn-neon-outline rounded-full px-8 py-3.5 text-sm font-semibold flex items-center gap-2">
                  Book a Demo
                  <Sparkles className="h-4 w-4" />
                </button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          INDUSTRY CHALLENGES
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              The Insurance Industry&apos;s
              <br />
              <span className="text-gradient-warm">Biggest Challenges</span>
            </h2>
            <p className="max-w-2xl mx-auto text-slate-300 text-lg">
              Insurers face mounting pressure from fraud, inaccurate pricing models, slow claims processing, and lack of real-time risk data.
            </p>
          </motion.div>

          <motion.div {...staggerContainer} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: AlertTriangle,
                title: 'Insurance Fraud',
                desc: 'Billions lost annually to staged accidents, false claims, and misrepresented driving behavior.',
                color: 'text-neon-magenta',
                bg: 'bg-neon-magenta/10',
                border: 'neon-border-purple',
              },
              {
                icon: TrendingDown,
                title: 'Inaccurate Pricing',
                desc: 'Traditional models rely on demographics and history, missing real-time driver risk factors.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
                border: 'neon-border-purple',
              },
              {
                icon: Clock,
                title: 'Slow Claims Processing',
                desc: 'Manual investigation processes take weeks, delaying payouts and frustrating customers.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
                border: 'neon-border-cyan',
              },
              {
                icon: Database,
                title: 'No Real-Time Data',
                desc: 'Lack of live telematics means insurers can\'t assess risk dynamically or prevent incidents.',
                color: 'text-neon-teal',
                bg: 'bg-neon-teal/10',
                border: 'neon-border-cyan',
              },
            ].map((challenge, i) => (
              <motion.div
                key={challenge.title}
                {...fadeInUp}
                transition={{ delay: i * 0.1 }}
                className={`glass-card rounded-2xl p-6 ${challenge.border} hover:scale-105 transition-transform`}
              >
                <div className={`inline-flex rounded-xl ${challenge.bg} p-3 mb-4 ${challenge.color}`}>
                  <challenge.icon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{challenge.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{challenge.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          OUR UBI SOLUTION
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section-purple py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-purple mb-4">
              <Zap className="h-3 w-3" />
              How Telematics Solves Each Challenge
            </span>
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              The Complete
              <br />
              <span className="text-gradient">UBI Solution</span>
            </h2>
            <p className="max-w-2xl mx-auto text-slate-300 text-lg">
              GlonexTrack provides end-to-end telematics intelligence specifically designed for insurance use cases.
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-2">
            {[
              {
                icon: Gauge,
                title: 'Advanced Driver Scoring',
                desc: 'AI-powered UBI scoring analyzes acceleration, braking, cornering, speed, and time-of-day patterns. Generate risk scores from 0-100 that update in real-time, enabling dynamic premium adjustments.',
                features: ['G-force sensor analysis', 'Trip-by-trip scoring', 'Behavioral pattern recognition', 'Risk tier classification'],
                gradient: 'from-neon-cyan to-neon-purple',
              },
              {
                icon: Car,
                title: 'Automated Crash Reconstruction',
                desc: 'Instant crash detection with G-force sensors, accelerometer data, and camera footage. Automatically reconstruct incidents with precise location, speed, impact force, and video evidence.',
                features: ['Instant crash alerts', 'Impact force calculation', 'Video evidence capture', 'Automated incident reports'],
                gradient: 'from-neon-magenta to-neon-purple',
              },
              {
                icon: CreditCard,
                title: 'Pay-How-You-Drive (PHYD)',
                desc: 'Enable usage-based insurance models where premiums adjust based on actual driving behavior. Reward safe drivers with discounts while accurately pricing high-risk behaviors.',
                features: ['Dynamic premium calculation', 'Safe driver discounts', 'Mileage-based pricing', 'Real-time policy updates'],
                gradient: 'from-neon-purple to-neon-cyan',
              },
              {
                icon: Activity,
                title: 'Real-Time Risk Profiling',
                desc: 'Monitor driver behavior continuously and update risk profiles instantly. Detect dangerous patterns before they result in claims, enabling proactive interventions.',
                features: ['Live risk monitoring', 'Predictive analytics', 'Behavioral alerts', 'Risk trend analysis'],
                gradient: 'from-neon-teal to-neon-cyan',
              },
            ].map((solution, i) => (
              <motion.div
                key={solution.title}
                {...fadeInUp}
                transition={{ delay: i * 0.15 }}
                className="glass-card-heavy rounded-2xl p-8 neon-border-purple hover:scale-[1.02] transition-transform"
              >
                <div className={`inline-flex rounded-xl bg-gradient-to-br ${solution.gradient} p-4 mb-6 text-white`}>
                  <solution.icon className="h-7 w-7" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{solution.title}</h3>
                <p className="text-slate-300 mb-6 leading-relaxed">{solution.desc}</p>
                <ul className="space-y-2">
                  {solution.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="h-4 w-4 text-neon-cyan mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          KEY FEATURES GRID
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              Insurance-Specific
              <br />
              <span className="text-gradient-cool">Features</span>
            </h2>
            <p className="max-w-2xl mx-auto text-slate-300 text-lg">
              Everything you need to build, deploy, and scale usage-based insurance programs.
            </p>
          </motion.div>

          <motion.div {...staggerContainer} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Gauge,
                title: 'G-Force Sensors',
                desc: 'Precise accelerometer data for harsh braking, acceleration, and cornering detection.',
                color: 'text-neon-cyan',
              },
              {
                icon: BarChart3,
                title: 'Trip Scoring',
                desc: 'AI-powered scoring algorithm that evaluates each trip for risk factors and safety compliance.',
                color: 'text-neon-purple',
              },
              {
                icon: Car,
                title: 'Crash Detection',
                desc: 'Instant crash alerts with impact force, location, and automatic emergency response triggers.',
                color: 'text-neon-magenta',
              },
              {
                icon: FileCheck,
                title: 'Policy Integration',
                desc: 'Seamless API integration with your policy management systems for real-time premium updates.',
                color: 'text-neon-teal',
              },
              {
                icon: Zap,
                title: 'Claims Automation',
                desc: 'Automated claims processing with video evidence, crash reconstruction, and fraud detection.',
                color: 'text-neon-cyan',
              },
              {
                icon: LineChart,
                title: 'Risk Dashboards',
                desc: 'Comprehensive analytics dashboards showing driver risk trends, claim predictions, and portfolio health.',
                color: 'text-neon-purple',
              },
              {
                icon: ShieldCheck,
                title: 'Fraud Detection',
                desc: 'AI algorithms detect staged accidents, false claims, and suspicious driving patterns.',
                color: 'text-neon-magenta',
              },
              {
                icon: Lock,
                title: 'Regulatory Compliance',
                desc: 'Built-in compliance with GDPR, insurance regulations, and data privacy requirements.',
                color: 'text-neon-teal',
              },
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                {...fadeInUp}
                transition={{ delay: i * 0.05 }}
                className="glass-card rounded-xl p-6 neon-border-cyan hover:scale-105 transition-transform group"
              >
                <div className={`inline-flex rounded-lg bg-neon-cyan/10 p-3 mb-4 ${feature.color} group-hover:bg-neon-cyan/20 transition-colors`}>
                  <feature.icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          TENANT INSURANCE MODULES
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section-purple py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-cyan mb-4">
              <Building2 className="h-3 w-3" />
              White-Label Tenant Module Stack
            </span>
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              Tenant Insurance
              <br />
              <span className="text-gradient">Modules Ready to Deploy</span>
            </h2>
            <p className="max-w-3xl mx-auto text-slate-300 text-lg">
              Launch each insurer with a focused module bundle: pricing intelligence,
              claims automation, underwriting controls, API integrations, and compliance
              guardrails.
            </p>
          </motion.div>

          <motion.div
            {...fadeInUp}
            className="mb-10 rounded-2xl border border-white/10 bg-white/5 p-6 text-left"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-neon-cyan">
              Signup Walkthrough Sequence
            </p>
            <p className="mt-1 text-sm font-semibold text-white">
              {TENANT_PACKAGE_LABELS.insurance}
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {insuranceOnboardingSequence.map((step, idx) => (
                <div
                  key={step}
                  className="rounded-xl border border-white/10 bg-slate-950/30 px-3 py-2"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-neon-purple">
                    Sequence {idx + 1}
                  </p>
                  <p className="mt-1 text-xs text-slate-300">{step}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {insurancePackageModules.map((moduleKey) => (
                <span
                  key={moduleKey}
                  className="rounded-full border border-neon-cyan/40 bg-neon-cyan/10 px-3 py-1 text-[11px] font-medium text-neon-cyan"
                >
                  {insurancePackageModuleLabel[moduleKey] ?? moduleKey}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div {...staggerContainer} className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {[
              {
                icon: AlertTriangle,
                title: 'Crash & Claims Automation',
                desc: 'Detect incidents instantly and auto-build first-notice-of-loss packets with location, force vectors, and event context.',
                points: [
                  'Instant SOS and crash event triggers',
                  'Digital twin crash reconstruction timeline',
                  'Auto-generated claim evidence payloads',
                ],
                color: 'text-neon-magenta',
              },
              {
                icon: LineChart,
                title: 'Risk Portfolio Command Center',
                desc: 'Monitor live portfolio exposure across products, territories, and tenant books with predictive claim trend analysis.',
                points: [
                  'Portfolio heatmaps and claim severity trends',
                  'Cohort-level risk drift monitoring',
                  'Early-warning alerts for risk spikes',
                ],
                color: 'text-neon-cyan',
              },
              {
                icon: Target,
                title: 'Underwriting Workbench',
                desc: 'Apply behavior-driven underwriting models using real trip telemetry, policy history, and configurable rating logic.',
                points: [
                  'Trip score and behavior-factor inputs',
                  'Renewal scenario simulation before pricing',
                  'Automated premium adjustment suggestions',
                ],
                color: 'text-neon-purple',
              },
              {
                icon: Database,
                title: 'Insurance API Gateway',
                desc: 'Connect policy, claims, and CRM platforms with robust APIs, webhook streams, and tenant-level API status visibility.',
                points: [
                  'REST and webhook pipelines for policy events',
                  'Tenant API status dashboards and alerts',
                  'Connectors for Guidewire and Salesforce',
                ],
                color: 'text-neon-teal',
              },
              {
                icon: ShieldCheck,
                title: 'Fraud & Compliance Guardrails',
                desc: 'Detect anomalies early while enforcing privacy and governance controls aligned with global insurance requirements.',
                points: [
                  'Fraud scoring from crash and trip anomalies',
                  'POPIA and GDPR-ready data controls',
                  'Audit trails, consent logs, and retention policies',
                ],
                color: 'text-neon-cyan',
              },
              {
                icon: Award,
                title: 'Leaderboard & Incentive Engine',
                desc: 'Drive safer behavior with transparent scorecards for drivers, brokers, and teams tied to incentive programs.',
                points: [
                  'Tenant-scoped leaderboards by branch and segment',
                  'Safe-driving reward and discount triggers',
                  'Role-based visibility for insurer operations teams',
                ],
                color: 'text-neon-magenta',
              },
            ].map((module, i) => (
              <motion.div
                key={module.title}
                {...fadeInUp}
                transition={{ delay: i * 0.08 }}
                className="glass-card-heavy rounded-2xl p-7 neon-border-cyan hover:scale-[1.02] transition-transform"
              >
                <div className={`inline-flex rounded-xl bg-neon-cyan/10 p-3 mb-4 ${module.color}`}>
                  <module.icon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{module.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{module.desc}</p>
                <ul className="mt-5 space-y-2">
                  {module.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="h-4 w-4 text-neon-cyan mt-0.5 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          USE CASES
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section-magenta py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              Insurance Use Cases
              <br />
              <span className="text-gradient-warm">Across All Segments</span>
            </h2>
            <p className="max-w-2xl mx-auto text-slate-300 text-lg">
              From personal auto to commercial fleets, GlonexTrack powers UBI programs across the insurance spectrum.
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-2">
            {[
              {
                icon: Car,
                title: 'Motor Insurance',
                desc: 'Personal auto insurance with pay-how-you-drive models. Offer discounts to safe drivers, accurately price high-risk behaviors, and reduce claims through proactive risk management.',
                benefits: ['30% reduction in claims', '25% better risk segmentation', '40% fraud reduction', 'Customer retention improvement'],
                gradient: 'from-neon-cyan to-neon-purple',
              },
              {
                icon: Truck,
                title: 'Commercial Fleet Insurance',
                desc: 'Comprehensive fleet insurance with driver behavior monitoring, crash detection, and automated claims. Reduce premiums for well-managed fleets and identify high-risk drivers.',
                benefits: ['Fleet-wide risk scoring', 'Driver coaching integration', 'Automated incident reporting', 'Reduced fleet premiums'],
                gradient: 'from-neon-purple to-neon-magenta',
              },
              {
                icon: Clock,
                title: 'Short-Term Insurance',
                desc: 'Flexible insurance for rental cars, delivery vehicles, and temporary coverage. Real-time risk assessment enables dynamic pricing and instant policy activation.',
                benefits: ['Instant policy activation', 'Per-trip pricing', 'Real-time risk assessment', 'Automated renewals'],
                gradient: 'from-neon-magenta to-neon-purple',
              },
              {
                icon: Building2,
                title: 'Reinsurance Analytics',
                desc: 'Advanced analytics for reinsurers to assess portfolio risk, predict claim frequency, and optimize underwriting strategies using aggregated telematics data.',
                benefits: ['Portfolio risk analysis', 'Predictive claim modeling', 'Underwriting optimization', 'Data-driven reinsurance'],
                gradient: 'from-neon-teal to-neon-cyan',
              },
            ].map((useCase, i) => (
              <motion.div
                key={useCase.title}
                {...fadeInUp}
                transition={{ delay: i * 0.15 }}
                className="glass-card-heavy rounded-2xl p-8 neon-border-purple hover:scale-[1.02] transition-transform"
              >
                <div className={`inline-flex rounded-xl bg-gradient-to-br ${useCase.gradient} p-4 mb-6 text-white`}>
                  <useCase.icon className="h-7 w-7" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{useCase.title}</h3>
                <p className="text-slate-300 mb-6 leading-relaxed">{useCase.desc}</p>
                <div className="space-y-2">
                  {useCase.benefits.map((benefit) => (
                    <div key={benefit} className="flex items-center gap-2 text-sm text-slate-300">
                      <div className="h-1.5 w-1.5 rounded-full bg-neon-magenta" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          ROI & BENEFITS
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-cyan mb-4">
              <TrendingUp className="h-3 w-3" />
              Proven Results
            </span>
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              Measurable ROI
              <br />
              <span className="text-gradient">For Insurers</span>
            </h2>
            <p className="max-w-2xl mx-auto text-slate-300 text-lg">
              Insurance companies using GlonexTrack see dramatic improvements in claims reduction, pricing accuracy, and operational efficiency.
            </p>
          </motion.div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 mb-16">
            {[
              { value: '30%', label: 'Fewer Claims', icon: TrendingDown, color: 'text-neon-cyan' },
              { value: '25%', label: 'Better Risk Pricing', icon: Target, color: 'text-neon-purple' },
              { value: '60%', label: 'Faster Claims Processing', icon: Zap, color: 'text-neon-magenta' },
              { value: '40%', label: 'Fraud Reduction', icon: ShieldCheck, color: 'text-neon-teal' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                {...fadeInUp}
                transition={{ delay: i * 0.1 }}
                className="glass-card-heavy rounded-2xl p-8 text-center neon-border-cyan"
              >
                <div className={`inline-flex rounded-xl bg-neon-cyan/10 p-4 mb-4 ${stat.color}`}>
                  <stat.icon className="h-8 w-8" />
                </div>
                <div className="text-5xl font-bold text-gradient mb-2">{stat.value}</div>
                <div className="text-slate-300 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          <motion.div {...staggerContainer} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: DollarSign,
                title: 'Reduced Claims Costs',
                desc: 'Proactive risk management and driver coaching reduce claim frequency and severity, directly impacting loss ratios.',
                color: 'text-neon-cyan',
              },
              {
                icon: BarChart3,
                title: 'Improved Underwriting',
                desc: 'Real-time telematics data enables more accurate risk assessment, leading to better pricing and portfolio profitability.',
                color: 'text-neon-purple',
              },
              {
                icon: Users,
                title: 'Customer Retention',
                desc: 'UBI programs reward safe drivers with discounts, increasing satisfaction and reducing churn rates.',
                color: 'text-neon-magenta',
              },
              {
                icon: Clock,
                title: 'Operational Efficiency',
                desc: 'Automated crash detection and claims processing reduce manual investigation time and administrative overhead.',
                color: 'text-neon-teal',
              },
              {
                icon: ShieldCheck,
                title: 'Fraud Prevention',
                desc: 'AI-powered fraud detection identifies suspicious patterns, preventing fraudulent claims before they\'re processed.',
                color: 'text-neon-cyan',
              },
              {
                icon: Award,
                title: 'Competitive Advantage',
                desc: 'Differentiate your insurance products with innovative UBI programs that attract tech-savvy customers.',
                color: 'text-neon-purple',
              },
            ].map((benefit, i) => (
              <motion.div
                key={benefit.title}
                {...fadeInUp}
                transition={{ delay: i * 0.1 }}
                className="glass-card rounded-xl p-6 neon-border-purple hover:scale-105 transition-transform"
              >
                <div className={`inline-flex rounded-lg bg-neon-purple/10 p-3 mb-4 ${benefit.color}`}>
                  <benefit.icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          CTA SECTION
          ══════════════════════════════════════════════════════ */}
      <section id="demo" className="gradient-section-purple py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="glass-card-heavy rounded-3xl p-12 lg:p-16 text-center neon-border-purple">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-flex rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-purple mb-6"
            >
              <Sparkles className="h-3 w-3" />
              Ready to Transform Your Insurance Operations?
            </motion.div>

            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              Start Your Free Trial Today
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-slate-300 mb-8">
              Join leading insurers using GlonexTrack to reduce claims, improve pricing accuracy, and automate claims processing.
              No credit card required. Setup in 5 minutes.
            </p>

            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row mb-8">
              <Link href="/signup?industry=insurance&package=insurance">
                <button className="btn-neon rounded-full px-8 py-3.5 text-sm font-semibold flex items-center gap-2 shadow-lg">
                  Start Free Trial
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link href="/contact">
                <button className="btn-neon-outline rounded-full px-8 py-3.5 text-sm font-semibold flex items-center gap-2">
                  Schedule Demo
                  <Sparkles className="h-4 w-4" />
                </button>
              </Link>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-300">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-neon-cyan" />
                <span>14-day free trial</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-neon-cyan" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-neon-cyan" />
                <span>Setup in 5 minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-neon-cyan" />
                <span>Full API access</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
